import { AppIcon } from '@/components/ui/app-icon';
import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import * as React from 'react';
type IconShim = React.ComponentType<{ className?: string }>;
const ArrowUpRight: IconShim = (p) => (
  <AppIcon icon={iconArrowUpRight} {...p} />
);
const Building2: IconShim = (p) => <AppIcon icon={iconBuildings} {...p} />;
const CheckCircle: IconShim = (p) => (
  <AppIcon icon={iconCircleCheckSolid} {...p} />
);
const Loader2: IconShim = (p) => <AppIcon icon={iconSpinnerThird} {...p} />;
const Rocket: IconShim = (p) => <AppIcon icon={iconRocket} {...p} />;
const Users: IconShim = (p) => <AppIcon icon={iconUsers} {...p} />;
const Zap: IconShim = (p) => <AppIcon icon={iconBolt} {...p} />;
import { useTheme } from '@/hooks/use-theme.hook';
import { cn } from '@/lib/utils';
import { extraSeatNote } from '@/lib/card-trial';
import { getPlanCredits } from '@/lib/credits';
import type { TierGroup } from '@/constants/plan-feature-matrix';
import type { ExtraSeatInfo } from '@/types';
import {
  iconArrowUpRight,
  iconBolt,
  iconBuildings,
  iconCircleCheckSolid,
  iconCoins,
  iconRocket,
  iconSpinnerThird,
  iconUsers,
} from '@/lib/icons';

const TIER_GRADIENTS: Record<string, string> = {
  starter: 'from-emerald-800 via-teal-800 to-cyan-800',
  pro: 'from-blue-800 via-indigo-800 to-violet-800',
  scale: 'from-violet-800 via-purple-800 to-fuchsia-800',
};

const TIER_ICONS: Record<string, typeof Rocket> = {
  starter: Rocket,
  pro: Zap,
  scale: Building2,
};

interface PlanTierCardProps {
  group: TierGroup;
  interval: 'month' | 'year';
  currentPlanId?: string;
  processingPriceId: string | null;
  onCheckout: (
    priceId: string,
    planDetails?: { id: string; name: string; price?: number; seats?: number }
  ) => void;
  /** V2-only. When set, the card shows the seat allowance + per-seat
   *  add-on price + a link to /settings/team. Absent for legacy plans. */
  extraSeat?: ExtraSeatInfo | null;
  /** Locks every control while a parent surface owns a cross-card decision. */
  disabled?: boolean;
  /** Minimum quantity required to cover the organization's current members. */
  minimumSeats?: number;
  /** Verified checkout offer shown before redirect, such as trial win-back. */
  discountPercent?: number;
  showSeatManagementLink?: boolean;
  /** Card-backed trial: every plan can start today (credits now) or become
   *  the plan the trial rolls into ($0 today). */
  cardTrial?: {
    /** Trial end, pre-formatted, e.g. "Oct 13". */
    endsOn: string;
    /** The exact option the trial currently becomes: tier, interval, credits. */
    renewal: { plan: string; interval: string; credits: number };
    onStartToday: (priceId: string, planDetails: CardTrialPlanDetails) => void;
    onMakeRenewal: (priceId: string, planDetails: CardTrialPlanDetails) => void;
  };
}

type CardTrialPlanDetails = {
  id: string;
  name: string;
  price?: number;
  credits?: number;
  /** Seats to buy: the roster, never fewer than the plan includes. */
  seats?: number;
  extraSeats?: string;
};

const formatUsd = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export function PlanTierCard({
  group,
  interval,
  currentPlanId,
  processingPriceId,
  onCheckout,
  extraSeat,
  disabled = false,
  minimumSeats,
  discountPercent,
  showSeatManagementLink = true,
  cardTrial,
}: PlanTierCardProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const currentSubIndex = group.plans.findIndex((p) => p.id === currentPlanId);
  const initialPlanId =
    currentSubIndex >= 0 ? group.plans[currentSubIndex].id : group.plans[0]?.id;
  const [selectedPlanId, setSelectedPlanId] = useState(initialPlanId);

  useEffect(() => {
    if (currentSubIndex >= 0)
      setSelectedPlanId(group.plans[currentSubIndex].id);
  }, [currentSubIndex, group.plans]);

  useEffect(() => {
    if (!group.plans.some((p) => p.id === selectedPlanId)) {
      setSelectedPlanId(group.plans[0]?.id);
    }
  }, [group.plans, selectedPlanId]);

  const baseSeats = group.baseSeats ?? 1;

  const selectedPlan =
    group.plans.find((p) => p.id === selectedPlanId) ?? group.plans[0];
  if (!selectedPlan) return null;

  const priceObj =
    interval === 'month'
      ? selectedPlan.monthly_price
      : selectedPlan.yearly_price;
  const priceId = priceObj?.id ?? undefined;
  const credits = getPlanCredits(selectedPlan);
  const phones = Math.floor(credits / 10);
  const isCurrentPlan = selectedPlan.id === currentPlanId;
  const isCurrentTier = group.plans.some((p) => p.id === currentPlanId);
  const isProcessing = priceId === processingPriceId;
  const hasMultiplePlans = group.plans.length > 1;
  const gradient = TIER_GRADIENTS[group.category] ?? TIER_GRADIENTS.pro;
  const TierIcon = TIER_ICONS[group.category] ?? Zap;
  // The option Stripe will bill, not just its tier: another interval or
  // credit option of the same tier is a switch like any other plan.
  const isRenewalTier =
    !!cardTrial &&
    group.category === cardTrial.renewal.plan &&
    interval === cardTrial.renewal.interval &&
    credits === cardTrial.renewal.credits;
  const trialActionDisabled = disabled || isProcessing || !priceId;

  const seatPriceCents = extraSeat?.monthly_price_cents ?? 0;
  const showSeatInfo = !!extraSeat;
  const checkoutSeats = Math.max(baseSeats, minimumSeats ?? baseSeats);
  // A trial team keeps its (up to 3) seats: the checkout preflight rejects a
  // plan that can't hold the current members.
  const trialDetails: CardTrialPlanDetails = {
    id: selectedPlan.id,
    name: group.label,
    price: priceObj?.price,
    credits,
    seats: checkoutSeats,
    extraSeats: extraSeatNote(checkoutSeats, baseSeats, seatPriceCents),
  };
  const discountMultiplier =
    discountPercent != null ? Math.max(0, 1 - discountPercent / 100) : 1;
  const effectiveCyclePrice =
    priceObj?.price != null ? priceObj.price * discountMultiplier : undefined;
  const effectiveMonthlyEquivalent =
    effectiveCyclePrice == null
      ? undefined
      : interval === 'year'
        ? effectiveCyclePrice / 12
        : effectiveCyclePrice;
  // Extra seats are added by checkout when the roster exceeds the tier's
  // included seats, so the base price alone is not what gets charged. State
  // both parts rather than a single combined figure: `extraSeat` is priced per
  // MONTH while the yearly base covers a year, and quoting one blended total
  // would be asserting a precision we don't have.
  const extraSeatCount = Math.max(0, checkoutSeats - baseSeats);
  const extraSeatSuffix =
    extraSeatCount > 0 && seatPriceCents > 0
      ? `, plus ${extraSeatCount} extra seat${
          extraSeatCount === 1 ? '' : 's'
        } at ${formatUsd(seatPriceCents / 100)}/month`
      : '';
  const chargeLabel =
    effectiveCyclePrice == null
      ? 'Price unavailable'
      : interval === 'year'
        ? `${formatUsd(effectiveCyclePrice)} charged for the first year${extraSeatSuffix}`
        : `${formatUsd(effectiveCyclePrice)} charged monthly for the first 12 months${extraSeatSuffix}`;

  return (
    <div
      className={cn(
        'relative flex min-w-0 flex-col overflow-hidden rounded-xl border transition-all duration-200 motion-reduce:transition-none forced-colors:border-[CanvasText]',
        isDark ? 'bg-[#1a1a1a]' : 'bg-white',
        isCurrentPlan
          ? 'border-green-500'
          : group.isPopular
            ? 'border-[#3E8BFF]'
            : isDark
              ? 'border-white/10 hover:border-[#3E8BFF]'
              : 'border-gray-200 hover:border-[#3E8BFF]'
      )}
    >
      <div className={cn('relative bg-linear-to-br px-4 pt-4 pb-4', gradient)}>
        {cardTrial && (
          <div className='absolute top-3 right-3'>
            {isRenewalTier ? (
              <div className='rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-900 lg:px-2.5'>
                Your plan from {cardTrial.endsOn}
              </div>
            ) : group.category === 'scale' ? (
              <div className='rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white lg:px-2.5'>
                You&apos;re trying this now
              </div>
            ) : null}
          </div>
        )}
        {group.isPopular && !isCurrentPlan && !cardTrial && (
          <div className='absolute top-3 right-3'>
            <div className='rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-900 lg:px-2.5'>
              Recommended
            </div>
          </div>
        )}
        {isCurrentPlan && (
          <div className='absolute top-3 right-3'>
            <div className='flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-emerald-600 lg:px-2.5'>
              <CheckCircle className='h-3 w-3' />
              Current Plan
            </div>
          </div>
        )}

        <div className='mb-1.5 flex items-center gap-2.5'>
          <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-white/20'>
            <TierIcon className='h-4 w-4 text-white' />
          </div>
          <h3 className='text-lg font-semibold text-white'>{group.label}</h3>
        </div>

        <p className='text-xs text-white'>{group.description}</p>
      </div>

      <div className='flex flex-1 flex-col px-4 pt-4 pb-4'>
        <div>
          <div className='flex items-baseline gap-1'>
            <span className='text-2xl font-semibold tracking-tight text-foreground'>
              {effectiveMonthlyEquivalent != null
                ? formatUsd(effectiveMonthlyEquivalent)
                : 'Price unavailable'}
            </span>
            {effectiveMonthlyEquivalent != null && (
              <span className='text-xs text-muted-foreground'>/month</span>
            )}
          </div>
          {priceObj?.price != null && (
            <p className='mt-1 text-xs leading-5 text-muted-foreground'>
              {interval === 'year' ? (
                discountPercent != null ? (
                  <>
                    {formatUsd(effectiveCyclePrice!)} charged for your first
                    year after {discountPercent}% off. Then{' '}
                    {formatUsd(priceObj.price)} each year, before tax.
                  </>
                ) : (
                  <>
                    {formatUsd(priceObj.price)} charged each year, before tax.
                  </>
                )
              ) : discountPercent != null ? (
                <>
                  {formatUsd(effectiveCyclePrice!)} each month for your first 12
                  months after {discountPercent}% off. Then{' '}
                  {formatUsd(priceObj.price)} each month, before tax.
                </>
              ) : (
                <>{formatUsd(priceObj.price)} charged monthly, before tax.</>
              )}
            </p>
          )}
        </div>

        {hasMultiplePlans && (
          <div className='mt-4'>
            <Select
              value={selectedPlanId}
              onValueChange={setSelectedPlanId}
              disabled={disabled}
            >
              <SelectTrigger
                aria-label={`${group.label} monthly credit allowance`}
                className={cn(
                  'h-9 w-full rounded-lg px-3 text-sm font-medium transition-all motion-reduce:transition-none',
                  isDark
                    ? 'border-white/10 bg-white/5 hover:border-white/20'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                )}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                className={cn(
                  'rounded-lg',
                  isDark
                    ? 'border-white/10 bg-[#1a1a1a]'
                    : 'border-gray-200 bg-white'
                )}
              >
                {group.plans.map((plan) => {
                  const planCredits = getPlanCredits(plan);
                  return (
                    <SelectItem
                      key={plan.id}
                      value={plan.id}
                      className='text-sm'
                    >
                      <span className='flex items-center gap-1.5'>
                        <AppIcon
                          icon={iconCoins}
                          className='text-emerald-500'
                          style={{ fontSize: 14 }}
                        />
                        {planCredits.toLocaleString()}/mo
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}

        {credits > 0 && (
          <div className='mt-2'>
            {!hasMultiplePlans && (
              <p className='flex items-center gap-1.5 text-sm font-medium text-foreground'>
                <AppIcon
                  icon={iconCoins}
                  className='text-emerald-500'
                  style={{ fontSize: 14 }}
                />
                {credits.toLocaleString()}/mo
              </p>
            )}
            <p className='mt-0.5 text-xs text-muted-foreground'>
              {credits.toLocaleString()} emails or {phones.toLocaleString()}{' '}
              phones/mo
            </p>
          </div>
        )}

        {/* Seat info — read-only on /plans. Add or remove seats lives
            on /settings/team so this card stays a "choose a tier"
            surface rather than a config screen. */}
        {showSeatInfo && (
          <div className='mt-3 rounded-lg border bg-muted/30 px-3 py-2'>
            <div className='flex items-center gap-1.5 text-xs font-medium text-foreground'>
              <Users className='h-3.5 w-3.5 text-foreground/70' />
              {baseSeats} seat{baseSeats === 1 ? '' : 's'} included
            </div>
            <p className='mt-1 text-xs text-muted-foreground'>
              {checkoutSeats > baseSeats
                ? `${checkoutSeats} seats will cover your current members. `
                : ''}
              Each extra seat is {formatUsd(seatPriceCents / 100)}/month.
              {showSeatManagementLink && (
                <>
                  {' '}
                  <Link
                    to='/settings/team'
                    className='underline underline-offset-2 hover:text-foreground'
                  >
                    Manage in Team Settings
                    <ArrowUpRight className='ml-0.5 inline h-3 w-3' />
                  </Link>
                </>
              )}
            </p>
          </div>
        )}

        <div className='mt-4 flex-1'>
          {group.previousTier && (
            <p className='mb-1.5 text-xs text-muted-foreground italic'>
              Everything in {group.previousTier}, plus...
            </p>
          )}
          <ul className='space-y-1'>
            {group.highlights.map((feature) => (
              <li
                key={feature}
                className='flex items-start gap-2 text-sm text-foreground'
              >
                {/* Solid circle-check in the brand emerald — a single crisp
                 * glyph. Replaces the old hand-rolled ring+check whose thin
                 * low-opacity ring read as weak/washed-out on the dark cards. */}
                <CheckCircle className='mt-0.5 h-4 w-4 shrink-0 text-emerald-500' />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {cardTrial ? (
          <div className='mt-5'>
            <Button
              variant='brand'
              className='h-10 w-full text-sm font-semibold'
              disabled={trialActionDisabled}
              onClick={() =>
                priceId && cardTrial.onStartToday(priceId, trialDetails)
              }
            >
              {isProcessing ? (
                <Loader2 className='mr-2 h-4 w-4 motion-safe:animate-spin' />
              ) : null}
              Start {group.label} today
            </Button>
            <p className='mt-1.5 text-center text-xs text-muted-foreground'>
              {credits > 0
                ? `Unlocks ${credits.toLocaleString()} credits now`
                : 'Starts your plan now'}
            </p>
            <div className='mt-3 border-t border-border pt-3 text-center text-xs'>
              {isRenewalTier ? (
                <span className='inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400'>
                  <CheckCircle className='h-3.5 w-3.5' />
                  Starts automatically on {cardTrial.endsOn}
                </span>
              ) : (
                <button
                  type='button'
                  disabled={trialActionDisabled}
                  onClick={() =>
                    priceId && cardTrial.onMakeRenewal(priceId, trialDetails)
                  }
                  className='text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50'
                >
                  {group.category === 'scale'
                    ? `Or keep Enterprise after ${cardTrial.endsOn} · $0 today`
                    : `Or switch to ${group.label} on ${cardTrial.endsOn} · $0 today`}
                </button>
              )}
            </div>
          </div>
        ) : (
          /* CTA: the Button's default, the black primary button. */
          <Button
            className='mt-5 h-9 w-full text-sm font-medium transition-all duration-200 motion-reduce:transition-none'
            disabled={disabled || isCurrentPlan || isProcessing || !priceId}
            variant={isCurrentPlan ? 'secondary' : 'default'}
            aria-label={
              isCurrentPlan
                ? `${group.label} is your current plan`
                : isProcessing
                  ? `Opening ${group.label} checkout, ${chargeLabel}`
                  : `Choose ${group.label} plan, ${chargeLabel}`
            }
            onClick={() => {
              if (priceId && !isCurrentPlan) {
                // Seats ride along ONLY when the trial-expired gate rendered
                // this card (it passes minimumSeats so a team already using
                // trial seats isn't sold a plan that can't hold them). On
                // /plans neither gate prop is set and seats are omitted — the
                // backend defaults to the tier's base_seats, exactly as today;
                // seat adjustments happen on /settings/team after checkout.
                onCheckout(priceId, {
                  id: selectedPlan.id,
                  name: selectedPlan.name,
                  price: priceObj?.price,
                  ...(minimumSeats != null || discountPercent != null
                    ? { seats: checkoutSeats }
                    : {}),
                });
              }
            }}
          >
            {isCurrentPlan ? (
              <>
                <CheckCircle className='mr-2 h-4 w-4' />
                Current plan
              </>
            ) : isProcessing ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 motion-safe:animate-spin' />
                Opening {group.label} checkout...
              </>
            ) : isCurrentTier ? (
              'Change plan'
            ) : (
              `Choose ${group.label}`
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
